import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Chip,
  Skeleton,
  Alert,
  Stack,
  Fade,
  Tooltip,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SyncIcon from '@mui/icons-material/Sync';
import Box from '@mui/material/Box';
import { useScrapeNotifications } from '../hooks/useScrapeNotifications';
import useResponsive from '../hooks/useResponsive';
import { HIDDEN_ON_MOBILE } from '../constants/responsive';

const BANK_ACCOUNTS_QUERY = gql`
  query BankAccounts {
    bankAccounts {
      id
      bankName
      accountNumber
      hasScraperSupport
    }
  }
`;

const TRANSACTIONS_QUERY = gql`
  query Transactions(
    $bankAccountId: Int
    $movementType: String
    $isFacturado: Boolean
    $flow: String
    $dateFrom: Date
    $dateTo: Date
    $categoryIds: [Int]
    $offset: Int
    $limit: Int
  ) {
    transactions(
      bankAccountId: $bankAccountId
      movementType: $movementType
      isFacturado: $isFacturado
      flow: $flow
      dateFrom: $dateFrom
      dateTo: $dateTo
      categoryIds: $categoryIds
      offset: $offset
      limit: $limit
    ) {
      items {
        id
        date
        bankDescription
        amount
        movementType
        isFacturado
        hidden
        isCreditCardPayment
        isPeerToPeerTransfer
        category {
          id
          name
          color
        }
        bankAccount {
          bankName
          accountNumber
        }
      }
      totalCount
    }
  }
`;

const CATEGORIES_QUERY = gql`
  query Categories {
    categories {
      id
      name
      color
    }
  }
`;

const UNCATEGORIZED_COUNT_QUERY = gql`
  query UncategorizedCount {
    transactions(hasCategory: false, limit: 0) {
      totalCount
    }
  }
`;

const UPDATE_TRANSACTION_CATEGORY = gql`
  mutation UpdateTransactionCategory($transactionId: Int!, $categoryId: Int) {
    updateTransactionCategory(transactionId: $transactionId, categoryId: $categoryId) {
      transaction {
        id
        category {
          id
          name
          color
        }
      }
    }
  }
`;

const UPDATE_TRANSACTION_HIDDEN = gql`
  mutation UpdateTransactionHidden($transactionId: Int!, $hidden: Boolean!) {
    updateTransactionHidden(transactionId: $transactionId, hidden: $hidden) {
      transaction {
        id
        hidden
      }
    }
  }
`;

const TRIGGER_SCRAPE = gql`
  mutation TriggerScrape {
    triggerScrape {
      batchId
    }
  }
`;

const formatCLP = (amount) => {
  const num = parseFloat(amount);
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  const formatted = abs.toLocaleString('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${sign}$${formatted}`;
};

const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const COLUMNS = [
  { label: 'Fecha', width: '10%', alwaysVisible: true },
  { label: 'Tipo', width: '10%', alwaysVisible: false },
  { label: 'Monto', width: '12%', align: 'right', alwaysVisible: true },
  { label: 'Descripción', width: '30%', alwaysVisible: true },
  { label: 'Categoría', width: '14%', alwaysVisible: true },
  { label: 'Facturado', width: '8%', align: 'center', alwaysVisible: false },
  { label: 'TEF', width: '6%', align: 'center', alwaysVisible: false },
  { label: 'Visible', width: '6%', align: 'center', alwaysVisible: false },
];

export default function Transactions() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [bankAccountId, setBankAccountId] = useState('');
  const [movementType, setMovementType] = useState('');
  const [isFacturado, setIsFacturado] = useState('');
  const [flow, setFlow] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [scraping, setScraping] = useState(false);

  const { isMobile } = useResponsive();

  const { data: bankData } = useQuery(BANK_ACCOUNTS_QUERY);
  const { data: catData } = useQuery(CATEGORIES_QUERY);
  const { data: uncatData, refetch: refetchUncatCount } = useQuery(UNCATEGORIZED_COUNT_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateTransactionCategory] = useMutation(UPDATE_TRANSACTION_CATEGORY);
  const [updateTransactionHidden] = useMutation(UPDATE_TRANSACTION_HIDDEN);
  const [triggerScrape] = useMutation(TRIGGER_SCRAPE);
  const { notification, clearNotification } = useScrapeNotifications();

  const categories = catData?.categories || [];
  const uncategorizedCount = uncatData?.transactions?.totalCount || 0;
  const hasAnyScraperSupport = bankData?.bankAccounts?.some((a) => a.hasScraperSupport) ?? false;

  const variables = {
    offset: page * rowsPerPage,
    limit: rowsPerPage,
    ...(bankAccountId && { bankAccountId: parseInt(bankAccountId) }),
    ...(movementType && { movementType }),
    ...(isFacturado !== '' && { isFacturado: isFacturado === 'true' }),
    ...(flow && { flow }),
    ...(dateFrom && { dateFrom: dateFrom.format('YYYY-MM-DD') }),
    ...(dateTo && { dateTo: dateTo.format('YYYY-MM-DD') }),
    ...(selectedCategoryIds.length > 0 && { categoryIds: selectedCategoryIds }),
  };

  const { data, loading, error, refetch } = useQuery(TRANSACTIONS_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network',
  });

  const transactions = data?.transactions?.items || [];
  const totalCount = data?.transactions?.totalCount || 0;

  // Reset scraping state when WebSocket notification arrives
  useEffect(() => {
    if (notification) setScraping(false);
  }, [notification]);

  const handleClearFilters = () => {
    setBankAccountId('');
    setMovementType('');
    setIsFacturado('');
    setFlow('');
    setDateFrom(null);
    setDateTo(null);
    setSelectedCategoryIds([]);
    setPage(0);
  };

  const hasActiveFilters =
    bankAccountId || movementType || isFacturado !== '' || flow || dateFrom || dateTo || selectedCategoryIds.length > 0;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ReceiptLongIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Transacciones</Typography>
        </Stack>
        <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Tooltip
            title={!hasAnyScraperSupport ? 'Actualmente no podemos extraer datos de tus bancos' : ''}
            arrow
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={() => { triggerScrape(); setScraping(true); }}
                disabled={scraping || !hasAnyScraperSupport}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                {scraping ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              uncategorizedCount === 0
                ? 'Todas las transacciones están categorizadas'
                : `${uncategorizedCount} sin categorizar`
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AutoFixHighIcon />}
                component={Link}
                to="/classify"
                disabled={uncategorizedCount === 0}
                sx={{ color: 'primary.dark', fontWeight: 700, flex: { xs: 1, sm: 'none' } }}
              >
                Clasificar
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Notification banner */}
      {notification && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => { refetch(); refetchUncatCount(); clearNotification(); }}
            >
              Recargar página
            </Button>
          }
        >
          Datos actualizados — {notification.message} | Recarga la página para ver los cambios.
        </Alert>
      )}

      {/* Filter Bar */}
      <Paper
        sx={{
          p: 2.5,
          mb: 3,
          borderLeft: '3px solid',
          borderLeftColor: 'secondary.main',
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'flex-end' }}
            flexWrap="wrap"
          >
            {/* Bank Account */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 200 } }}>
              <InputLabel>Cuenta</InputLabel>
              <Select
                value={bankAccountId}
                label="Cuenta"
                onChange={(e) => { setBankAccountId(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Todas</MenuItem>
                {bankData?.bankAccounts?.map((ba) => (
                  <MenuItem key={ba.id} value={ba.id}>
                    {ba.bankName} — ****{ba.accountNumber.slice(-4)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Category multi-select */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 200 } }}>
              <InputLabel>Categoría</InputLabel>
              <Select
                multiple
                value={selectedCategoryIds}
                label="Categoría"
                onChange={(e) => { setSelectedCategoryIds(e.target.value); setPage(0); }}
                renderValue={(selected) =>
                  categories
                    .filter((c) => selected.includes(parseInt(c.id)))
                    .map((c) => c.name)
                    .join(', ')
                }
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={parseInt(cat.id)}>
                    <Box
                      sx={{
                        width: 10, height: 10, borderRadius: '50%',
                        backgroundColor: cat.color, mr: 1.5, flexShrink: 0,
                        display: 'inline-block',
                      }}
                    />
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range */}
            <DatePicker
              label="Desde"
              value={dateFrom}
              onChange={(v) => { setDateFrom(v); setPage(0); }}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 } } } }}
            />
            <DatePicker
              label="Hasta"
              value={dateTo}
              onChange={(v) => { setDateTo(v); setPage(0); }}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', md: 160 } } } }}
            />

            {/* Movement Type */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 150 } }}>
              <InputLabel>Tipo movimiento</InputLabel>
              <Select
                value={movementType}
                label="Tipo movimiento"
                onChange={(e) => {
                  setMovementType(e.target.value);
                  if (e.target.value !== 'CREDIT') setIsFacturado('');
                  setPage(0);
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="DEBIT">Débito</MenuItem>
                <MenuItem value="CREDIT">Crédito</MenuItem>
              </Select>
            </FormControl>

            {/* Facturado — only visible when CREDIT */}
            <Fade in={movementType === 'CREDIT'} unmountOnExit>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 140 } }}>
                <InputLabel>Facturado</InputLabel>
                <Select
                  value={isFacturado}
                  label="Facturado"
                  onChange={(e) => { setIsFacturado(e.target.value); setPage(0); }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Fade>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            {/* Flow toggle */}
            <ToggleButtonGroup
              value={flow}
              exclusive
              onChange={(_, v) => { setFlow(v || ''); setPage(0); }}
              size="small"
            >
              <ToggleButton value="">Todos</ToggleButton>
              <ToggleButton value="INGRESO">
                <TrendingUpIcon sx={{ mr: 0.5, fontSize: 18 }} /> Ingresos
              </ToggleButton>
              <ToggleButton value="EGRESO">
                <TrendingDownIcon sx={{ mr: 0.5, fontSize: 18 }} /> Egresos
              </ToggleButton>
            </ToggleButtonGroup>

            {hasActiveFilters && (
              <Fade in>
                <Button
                  size="small"
                  startIcon={<FilterAltOffIcon />}
                  onClick={handleClearFilters}
                  sx={{ ml: 'auto' }}
                >
                  Limpiar filtros
                </Button>
              </Fade>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar transacciones: {error.message}
        </Alert>
      )}

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{ overflowX: 'auto' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: '#EDF0F3',
                '& .MuiTableCell-head': {
                  color: 'text.primary',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  py: 1.5,
                },
              }}
            >
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.label}
                  align={col.align || 'left'}
                  width={col.width}
                  sx={col.alwaysVisible ? {} : HIDDEN_ON_MOBILE}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data
              ? Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.label} sx={col.alwaysVisible ? {} : HIDDEN_ON_MOBILE}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : transactions.map((tx) => (
                  <Fade in key={tx.id}>
                    <TableRow
                      sx={{
                        transition: 'all 0.2s ease',
                        opacity: tx.hidden ? 0.45 : 1,
                      }}
                    >
                      {/* Fecha */}
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatDate(tx.date)}
                      </TableCell>

                      {/* Tipo */}
                      <TableCell sx={HIDDEN_ON_MOBILE}>
                        <Chip
                          label={tx.movementType === 'DEBIT' ? 'Débito' : 'Crédito'}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            ...(tx.movementType === 'DEBIT'
                              ? {
                                  backgroundColor: 'primary.main',
                                  color: '#fff',
                                }
                              : {
                                  backgroundColor: 'secondary.main',
                                  color: 'primary.dark',
                                }),
                          }}
                        />
                      </TableCell>

                      {/* Monto */}
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          color: parseFloat(tx.amount) < 0 ? '#D4710A' : 'success.main',
                        }}
                      >
                        {formatCLP(tx.amount)}
                      </TableCell>

                      {/* Descripción */}
                      <TableCell>
                        <Tooltip title={tx.bankDescription} arrow placement="top-start">
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: { xs: 120, md: 320 } }}
                          >
                            {tx.bankDescription}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      {/* Categoría */}
                      <TableCell>
                        <Select
                          value={tx.category?.id || ''}
                          onChange={(e) => {
                            const catId = e.target.value || null;
                            updateTransactionCategory({
                              variables: {
                                transactionId: parseInt(tx.id),
                                categoryId: catId ? parseInt(catId) : null,
                              },
                            });
                          }}
                          size="small"
                          displayEmpty
                          variant="standard"
                          disableUnderline
                          sx={{
                            fontSize: '0.75rem',
                            minWidth: { xs: 70, md: 100 },
                            '& .MuiSelect-select': { py: 0.5 },
                          }}
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  —
                                </Typography>
                              );
                            }
                            const cat = categories.find((c) => c.id === selected);
                            return cat ? (
                              <Chip
                                label={cat.name}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  borderColor: cat.color,
                                  color: cat.color,
                                }}
                              />
                            ) : '—';
                          }}
                        >
                          <MenuItem value="">
                            <em>Sin categoría</em>
                          </MenuItem>
                          {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: cat.color,
                                    flexShrink: 0,
                                  }}
                                />
                                {cat.name}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>

                      {/* Facturado */}
                      <TableCell align="center" sx={HIDDEN_ON_MOBILE}>
                        {tx.isFacturado ? (
                          <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <HighlightOffIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.4 }} />
                        )}
                      </TableCell>

                      {/* TEF */}
                      <TableCell align="center" sx={HIDDEN_ON_MOBILE}>
                        {tx.isPeerToPeerTransfer ? (
                          <CheckCircleOutlineIcon sx={{ color: 'info.main', fontSize: 20 }} />
                        ) : (
                          <HighlightOffIcon sx={{ color: 'text.secondary', fontSize: 20, opacity: 0.4 }} />
                        )}
                      </TableCell>

                      {/* Visible */}
                      <TableCell align="center" sx={HIDDEN_ON_MOBILE}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateTransactionHidden({
                              variables: {
                                transactionId: parseInt(tx.id),
                                hidden: !tx.hidden,
                              },
                            })
                          }
                        >
                          {tx.hidden ? (
                            <VisibilityOffIcon sx={{ fontSize: 20, color: 'text.secondary', opacity: 0.6 }} />
                          ) : (
                            <VisibilityIcon sx={{ fontSize: 20, color: 'success.main' }} />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </Fade>
                ))}

            {/* Empty state */}
            {!loading && transactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 8 }}>
                  <ReceiptLongIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    No hay transacciones
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={isMobile ? 'Filas' : 'Filas por página'}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{
            borderTop: '1px solid',
            borderTopColor: 'divider',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontWeight: 600,
              fontSize: '0.8rem',
            },
          }}
        />
      </TableContainer>
    </Container>
  );
}
